import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DotSpinner,
  PlusMdIcon,
  RadioGroup,
  RadioGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@crono-one/design-system';
import Avatar from 'components/Avatar';
import SearchInputView from 'components/SearchInputView';
import { Account } from 'crono-fe-common/types/account';
import { AccountLinkedin } from 'crono-fe-common/types/accountLinkedin';
import { getImageFromUrl, stringAvatarAccount } from 'crono-fe-common/utils';
import useSearchAccountsInfinite from 'hooks/services/account/useSearchAccountsInfinite';
import { useEffect, useRef, useState } from 'react';

const createCompanyLabel = (search: string) => {
  const trimmed = search.trim();
  return trimmed ? `Create company: ${trimmed}` : 'Create company';
};

export interface CompanySearchModalProps {
  title: string;
  predefinedAccounts?: AccountLinkedin[];
  submitButtonText?: string;
  /** The picked company is being saved: the modal can't be closed meanwhile. */
  isSubmitting?: boolean;
  /** Gets the picked company; the caller closes the modal once it's done with it. */
  submit: (account: Account | AccountLinkedin) => void;
  close: () => void;
  openCreateCompany?: (searchValue?: string) => void;
}

const CompanySearchModal = ({
  title,
  predefinedAccounts,
  submitButtonText = 'Submit',
  isSubmitting,
  submit,
  close,
  openCreateCompany,
}: CompanySearchModalProps) => {
  const [search, setSearch] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const {
    data: accounts,
    isInitialLoading: accountsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSearchAccountsInfinite(
    {
      name: search.trim(),
      status: null,
      // Relevance only earns its keep once there's a term to rank against
      // (a whitespace-only value is truthy but has nothing to rank); keep
      // the modal's original newest-first order otherwise.
      sort: search.trim() ? 'RelevanceFirst' : 'CreatedDateDesc',
    },
    !predefinedAccounts,
  );

  const options: Array<Account | AccountLinkedin> =
    predefinedAccounts ??
    accounts?.pages.flatMap((page) => page.data?.data ?? []) ??
    [];
  const selectedAccount =
    options.find((account) => account.objectId === selectedId) ?? null;

  const handleSubmit = () => {
    if (!selectedAccount) return;
    submit(selectedAccount);
  };

  const sentinelRef = useRef<HTMLDivElement>(null);

  // The list scrolls inside the dialog body, which clips the sentinel, so the
  // viewport root sees it only once it's scrolled into view.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    });
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  /*
   * The tier has to go on the portal: it carries `isolate`, so a z-index on the
   * content or the overlay is resolved inside that stacking context and can
   * never lift the dialog over the callers' BlurBackgroundDiv overlays at 5000.
   * 5005 keeps it under the in-dialog tooltip at 5006.
   */
  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen && !isSubmitting) close();
      }}
    >
      <DialogContent className="w-131 h-144 gap-6" portalClassName="z-5005">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {!predefinedAccounts && !accountsLoading && (
          <SearchInputView
            className="w-50"
            placeholder="Search..."
            value={search}
            disabled={isSubmitting}
            onChange={setSearch}
            onClear={() => setSearch('')}
          />
        )}

        {accountsLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <DotSpinner />
          </div>
        ) : (
          <DialogBody>
            {openCreateCompany && (
              <button
                type="button"
                className="group shrink-0 flex items-center gap-3 w-full p-3 border-0 rounded-lg bg-primary-100 font-sans text-left text-gray-900 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isSubmitting}
                onClick={() => openCreateCompany(search)}
              >
                <span className="flex items-center justify-center rounded-full p-1.5 text-white bg-primary-300 transition-colors group-enabled:group-hover:bg-primary-700">
                  <PlusMdIcon />
                </span>
                <span className="text-sm font-medium">
                  {createCompanyLabel(search)}
                </span>
              </button>
            )}

            {options.length === 0 && (
              <p className="text-sm text-gray-700">No companies found</p>
            )}

            <RadioGroup
              aria-label="Companies"
              disabled={isSubmitting}
              value={selectedId}
              onValueChange={(objectId) => setSelectedId(objectId as string)}
              className="gap-0"
            >
              {options.map((account) => (
                <label
                  key={account.objectId}
                  className="shrink-0 min-w-0 flex items-center justify-between gap-3 p-3 cursor-pointer border-b border-neutral-300 transition-colors last:border-none hover:bg-gray-100"
                >
                  <span className="flex-1 flex items-center gap-3 overflow-hidden">
                    <Avatar
                      name={account.name}
                      src={
                        account.website
                          ? getImageFromUrl(account.website, null)
                          : null
                      }
                      fallbackText={stringAvatarAccount}
                    />
                    <Tooltip delayDuration={800}>
                      <TooltipTrigger asChild>
                        <span className="truncate text-sm font-medium">
                          {account.name}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="z-5006">
                        {account.name}
                      </TooltipContent>
                    </Tooltip>
                  </span>
                  <RadioGroupItem value={account.objectId} />
                </label>
              ))}
            </RadioGroup>

            {!predefinedAccounts && <div ref={sentinelRef} />}

            {isFetchingNextPage && (
              <div className="flex shrink-0 justify-center py-3">
                <DotSpinner />
              </div>
            )}
          </DialogBody>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            tone="gray"
            size="lg"
            className="min-w-40"
            disabled={isSubmitting}
            onClick={close}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            className="min-w-40"
            loading={isSubmitting}
            disabled={!selectedAccount}
            onClick={handleSubmit}
          >
            {submitButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompanySearchModal;
